import { motion } from "framer-motion";
import { Rocket, Target, Sparkles, ArrowUpRight, LifeBuoy } from "lucide-react";
import { Link } from "react-router-dom";
import { FloatingPlus } from "./Decor";
import { ConicAmbience } from "./Ambience";
import { smoothClick } from "../lib/scroll";

const services = [
  {
    id: "express",
    icon: Rocket,
    tag: "01 — Lancement Express",
    title: "Votre image en ligne ne reflète pas votre commerce ?",
    text: "Vous êtes soigné, accueillant, plein de caractère en vrai — mais en ligne, on ne le voit pas. Un site immersif qui rend enfin justice à votre univers et donne envie de venir.",
    bullets: [
      "Design sur-mesure fidèle à votre univers",
      "Site immersif, mobile-first, ultra-rapide",
      "Référencement local pour être trouvé",
      "Livraison en 7 à 10 jours",
    ],
    cta: "Recevoir mon aperçu gratuit",
    to: "/mockup-gratuit",
  },
  {
    id: "vitrine",
    icon: Target,
    tag: "02 — Site Vitrine Complet",
    title: "Votre entreprise mérite mieux ?",
    text: "Un site complet qui reflète votre professionnalisme et génère des demandes qualifiées. Idéal pour asseoir votre crédibilité.",
    bullets: [
      "Site multi-pages (5 à 8 pages)",
      "Blog intégré pour booster le SEO",
      "Référencement Google natif",
      "Formation à la gestion incluse",
    ],
    cta: "Obtenir un audit gratuit",
    featured: true,
  },
  {
    id: "surmesure",
    icon: Sparkles,
    tag: "03 — Projets Sur-Mesure",
    title: "Besoin d'une solution complexe ?",
    text: "E-commerce, applications web, systèmes de réservation — nous développons votre projet de A à Z avec un accompagnement stratégique.",
    bullets: [
      "E-commerce et paiements",
      "Applications web sur-mesure",
      "Systèmes de réservation",
      "Intégrations API & automatisation",
    ],
    cta: "Réserver un appel",
  },
];

export default function Services() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative overflow-hidden py-28 sm:py-40 px-5 sm:px-8 lg:px-12"
    >
      <ConicAmbience />
      <FloatingPlus x="3%" y="12%" size={16} delay={0.2} />
      <FloatingPlus x="96%" y="50%" size={18} delay={0.5} accent />
      <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16 sm:mb-20 items-end">
        <div className="lg:col-span-7">
          <motion.span
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            className="font-mono text-xs uppercase tracking-[0.25em] text-zinc-500 mb-6 block"
          >
            (02) — Services
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, ease: [0.2, 0.8, 0.2, 1] }}
            className="font-display uppercase text-5xl sm:text-6xl lg:text-7xl leading-[0.9] tracking-tighter"
          >
            Comment nous <span className="text-outline">pouvons</span> vous aider
          </motion.h2>
        </div>
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ delay: 0.15 }}
          className="lg:col-span-4 lg:col-start-9 text-zinc-400 text-base sm:text-lg leading-relaxed"
        >
          Trois manières de travailler ensemble, conçues pour s'adapter à votre étape et à votre ambition. Chaque format est pensé pour vous faire gagner du temps — et des clients.
        </motion.p>
      </div>

      <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-5">
        {services.map((s, i) => {
          const Icon = s.icon;
          const span = s.featured ? "lg:col-span-6" : "lg:col-span-3";
          return (
            <motion.article
              key={s.id}
              data-testid={`service-card-${s.id}`}
              data-cursor="hover"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: i * 0.08, ease: [0.2, 0.8, 0.2, 1] }}
              className={`tracing-card bento group relative p-7 sm:p-9 flex flex-col justify-between min-h-[420px] ${span} ${
                s.featured ? "lg:row-span-1" : ""
              }`}
            >
              <div>
                <div className="flex items-start justify-between mb-12">
                  <Icon className="w-6 h-6 text-[var(--accent)]" strokeWidth={1.5} />
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                    {s.tag}
                  </span>
                </div>
                <h3 className="font-display text-3xl sm:text-4xl tracking-tight leading-tight mb-5">
                  {s.title}
                </h3>
                <p className="text-zinc-400 text-sm sm:text-base leading-relaxed mb-6">
                  {s.text}
                </p>
                <ul className="space-y-2 mb-8">
                  {s.bullets.map((b) => (
                    <li
                      key={b}
                      className="flex items-start gap-2 text-sm text-zinc-300"
                    >
                      <span className="text-[var(--accent)] mt-1">→</span>
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
              {s.to ? (
                <Link
                  to={s.to}
                  data-testid={`service-cta-${s.id}`}
                  className="inline-flex items-center gap-2 font-body text-sm uppercase tracking-[0.18em] text-white group-hover:text-[var(--accent)] transition-colors"
                >
                  {s.cta}
                  <ArrowUpRight className="w-4 h-4" />
                </Link>
              ) : (
                <a
                  href="#contact"
                  onClick={smoothClick("contact")}
                  data-testid={`service-cta-${s.id}`}
                  className="inline-flex items-center gap-2 font-body text-sm uppercase tracking-[0.18em] text-white group-hover:text-[var(--accent)] transition-colors"
                >
                  {s.cta}
                  <ArrowUpRight className="w-4 h-4" />
                </a>
              )}
            </motion.article>
          );
        })}
      </div>

      {/* Maintenance — full-width 4th offer (after launch) */}
      <motion.article
        data-testid="service-card-maintenance"
        data-cursor="hover"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.7, delay: 0.05, ease: [0.2, 0.8, 0.2, 1] }}
        className="tracing-card bento group relative z-10 mt-5 p-7 sm:p-10 grid grid-cols-1 md:grid-cols-12 gap-8 items-start"
      >
        <div className="md:col-span-5 lg:col-span-4 flex flex-col gap-5">
          <div className="flex items-start justify-between">
            <LifeBuoy className="w-6 h-6 text-[var(--accent)]" strokeWidth={1.5} />
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
              04 — Maintenance &amp; SEO
            </span>
          </div>
          <h3 className="font-display text-3xl sm:text-4xl tracking-tight leading-[1.05]">
            Une fois en ligne, on continue.
          </h3>
          <p className="text-zinc-400 text-sm sm:text-base leading-relaxed max-w-md">
            Service mensuel optionnel pour garder votre site à jour, sécurisé et bien référencé — sans vous en occuper. Le premier mois est offert après chaque livraison.
          </p>
          <a
            href="#contact"
            onClick={smoothClick("contact")}
            data-testid="service-cta-maintenance"
            className="mt-2 inline-flex items-center gap-2 font-body text-sm uppercase tracking-[0.18em] text-white group-hover:text-[var(--accent)] transition-colors"
          >
            En savoir plus
            <ArrowUpRight className="w-4 h-4" />
          </a>
        </div>

        <div className="md:col-span-7 lg:col-span-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            {
              n: "01",
              title: "Mises à jour techniques",
              text: "Sécurité, dépendances, sauvegardes, monitoring — votre site reste rapide et protégé.",
            },
            {
              n: "02",
              title: "Rapport SEO mensuel",
              text: "Suivi des positions Google, Core Web Vitals, recommandations concrètes pour progresser.",
            },
            {
              n: "03",
              title: "1h de modifications",
              text: "Ajouts de contenu, retouches design, nouvelles sections — chaque mois, on s'adapte.",
            },
          ].map((b) => (
            <div
              key={b.n}
              className="border border-white/[0.06] rounded-xl p-5 bg-[#0a0a0a]/60 hover:bg-[#0d0d0d] transition-colors duration-500"
            >
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--accent)] mb-3 block">
                {b.n}
              </span>
              <div className="font-display text-lg tracking-tight mb-2">
                {b.title}
              </div>
              <p className="text-zinc-400 text-xs leading-relaxed">{b.text}</p>
            </div>
          ))}
        </div>
      </motion.article>
    </section>
  );
}
